import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { LogOut, Search, FileText, LayoutDashboard, Warehouse, Users, DollarSign, Forklift, Truck } from 'lucide-react';
import PlinthionLogo from './PlinthionLogo';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getNavLinks = () => {
    if (user?.role === 'customer') {
      return [
        { to: '/customer/search', label: 'Search', icon: Search },
        { to: '/customer/bookings', label: 'My Bookings', icon: FileText }
      ];
    } else if (user?.role === 'provider') {
      return [
        { to: '/provider/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { to: '/provider/warehouses', label: 'My Warehouses', icon: Warehouse },
        { to: '/provider/mhe', label: 'My MHEs', icon: Forklift },
        { to: '/provider/fleet', label: 'My Fleet', icon: Truck },
        { to: '/provider/manpower', label: 'My Manpower', icon: Users }
      ];
    } else if (user?.role === 'admin') {
      return [
        { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { to: '/admin/users', label: 'Users', icon: Users },
        { to: '/admin/warehouses', label: 'Warehouses', icon: Warehouse },
        { to: '/admin/transactions', label: 'Transactions', icon: DollarSign }
      ];
    }
    return [];
  };

  return (
    <nav style={{backgroundColor: '#16207B'}} className="border-b border-border shadow-sm">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center" data-testid="navbar-logo">
              <PlinthionLogo size="default" light={true} />
            </Link>

            <div className="hidden md:flex gap-1">
              {getNavLinks().map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                    location.pathname === link.to
                      ? 'bg-primary-foreground/10 text-primary-foreground'
                      : 'text-primary-foreground/80 hover:bg-primary-foreground/5 hover:text-primary-foreground'
                  }`}
                >
                  <link.icon className="h-4 w-4" strokeWidth={1.5} />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-primary-foreground">{user?.name}</div>
              <div className="text-xs text-primary-foreground/70 uppercase tracking-wider font-mono">
                {user?.role}
              </div>
            </div>
            <Button
              onClick={handleLogout}
              data-testid="logout-button"
              variant="ghost"
              size="sm"
              className="text-primary-foreground hover:bg-primary-foreground/10 rounded-sm"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;